/**
 * Test Database Part 4 - Chỉ chạy trong môi trường Dev
 * Test chức năng load database từ server (TN.db, DS.db, TL.db)
 * KHÔNG ảnh hưởng đến phần 1 (theory/images), phần 2 (YouTube videos), phần 3 (simulation videos)
 */

import React, { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Database, Download, CheckCircle, XCircle, Loader, RefreshCw, Upload, FileText, Play } from 'lucide-react'
import { bankQuestionService, BankQuizData } from '../services/bankQuestionService_moi'
import { teacherContentService } from '../services/teacher/TeacherContentService'
import toast from 'react-hot-toast'

const TestDatabasePart4: React.FC = () => {
  // Chỉ hiển thị trong môi trường Dev
  if (import.meta.env.PROD) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Không khả dụng</h1>
          <p className="text-gray-600">Trang này chỉ khả dụng trong môi trường Development</p>
        </div>
      </div>
    )
  }

  const navigate = useNavigate()
  const [teacherId, setTeacherId] = useState('')
  const [lop, setLop] = useState('11')
  const [chuong, setChuong] = useState('1')
  const [bai, setBai] = useState('1')
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadedFiles, setUploadedFiles] = useState<{ TN?: File; DS?: File; TL?: File }>({})
  const [uploadResults, setUploadResults] = useState<{ TN?: string; DS?: string; TL?: string }>({})
  const fileInputRefs = {
    TN: useRef<HTMLInputElement>(null),
    DS: useRef<HTMLInputElement>(null),
    TL: useRef<HTMLInputElement>(null)
  }
  const [results, setResults] = useState<{
    server?: { TN?: number; DS?: number; TL?: number }
    indexedDB?: { TN?: number; DS?: number; TL?: number }
    system?: { TN?: number; DS?: number; TL?: number }
    quiz?: BankQuizData
    error?: string
  }>({})

  const testServerDatabases = async () => {
    if (!teacherId) {
      alert('Vui lòng nhập Teacher ID')
      return
    }

    setLoading(true)
    setResults({})

    try {
      console.log('🧪 [Test Part 4] Testing server databases...')
      const serverDbs = await teacherContentService.getTeacherDatabases(teacherId, lop, chuong, bai)
      
      setResults(prev => ({
        ...prev,
        server: {
          TN: serverDbs.TN?.length || 0,
          DS: serverDbs.DS?.length || 0,
          TL: serverDbs.TL?.length || 0
        }
      }))

      console.log('✅ [Test Part 4] Server databases loaded:', {
        TN: serverDbs.TN ? `${serverDbs.TN.length} bytes` : 'null',
        DS: serverDbs.DS ? `${serverDbs.DS.length} bytes` : 'null',
        TL: serverDbs.TL ? `${serverDbs.TL.length} bytes` : 'null'
      })
    } catch (error: any) {
      console.error('❌ [Test Part 4] Error loading server databases:', error)
      setResults(prev => ({
        ...prev,
        error: `Server error: ${error.message}`
      }))
    } finally {
      setLoading(false)
    }
  }

  const testIndexedDBDatabases = async () => {
    if (!teacherId) {
      alert('Vui lòng nhập Teacher ID')
      return
    }

    setLoading(true)

    try {
      console.log('🧪 [Test Part 4] Testing IndexedDB databases...')
      const [tnDb, dsDb, tlDb] = await Promise.all([
        teacherContentService.loadDatabaseFromIndexedDB(teacherId, lop, chuong, bai, 'TN').catch(() => null),
        teacherContentService.loadDatabaseFromIndexedDB(teacherId, lop, chuong, bai, 'DS').catch(() => null),
        teacherContentService.loadDatabaseFromIndexedDB(teacherId, lop, chuong, bai, 'TL').catch(() => null)
      ])

      setResults(prev => ({
        ...prev,
        indexedDB: {
          TN: tnDb?.length || 0,
          DS: dsDb?.length || 0,
          TL: tlDb?.length || 0
        }
      }))

      console.log('✅ [Test Part 4] IndexedDB databases loaded:', {
        TN: tnDb ? `${tnDb.length} bytes` : 'null',
        DS: dsDb ? `${dsDb.length} bytes` : 'null',
        TL: tlDb ? `${tlDb.length} bytes` : 'null'
      })
    } catch (error: any) {
      console.error('❌ [Test Part 4] Error loading IndexedDB databases:', error)
      setResults(prev => ({
        ...prev,
        error: `IndexedDB error: ${error.message}`
      }))
    } finally {
      setLoading(false)
    }
  }

  const testSystemDatabases = async () => {
    setLoading(true)

    try {
      console.log('🧪 [Test Part 4] Testing system databases...')
      
      // Test load từ system (không có teacherId)
      const quiz = await bankQuestionService.getRandomQuestions(lop, chuong, bai, {
        thoiGianLamBai: 15,
        diemDat: 70
      })

      // Đếm số câu hỏi theo loại
      const tnCount = quiz.cauHoi.filter(q => q.type === 'trac_nghiem_nhieu_lua_chon' || q.type === 'multiple_choice').length
      const dsCount = quiz.cauHoi.filter(q => q.type === 'dung_sai' || q.type === 'true_false').length
      const tlCount = quiz.cauHoi.filter(q => q.type === 'tinh_toan' || q.type === 'short_answer').length

      setResults(prev => ({
        ...prev,
        system: {
          TN: tnCount,
          DS: dsCount,
          TL: tlCount
        },
        quiz
      }))

      console.log('✅ [Test Part 4] System databases loaded:', {
        totalQuestions: quiz.cauHoi.length,
        TN: tnCount,
        DS: dsCount,
        TL: tlCount
      })
    } catch (error: any) {
      console.error('❌ [Test Part 4] Error loading system databases:', error)
      setResults(prev => ({
        ...prev,
        error: `System error: ${error.message}`
      }))
    } finally {
      setLoading(false)
    }
  }

  const testTeacherQuiz = async () => {
    if (!teacherId) {
      alert('Vui lòng nhập Teacher ID')
      return
    }

    setLoading(true)

    try {
      console.log('🧪 [Test Part 4] Testing teacher quiz loading...')
      
      // Load từ server trước
      let tnDb: Uint8Array | null = null
      let dsDb: Uint8Array | null = null
      let tlDb: Uint8Array | null = null

      try {
        const serverDbs = await teacherContentService.getTeacherDatabases(teacherId, lop, chuong, bai)
        tnDb = serverDbs.TN
        dsDb = serverDbs.DS
        tlDb = serverDbs.TL
      } catch (e) {
        console.warn('⚠️ Server databases not found, trying IndexedDB...')
      }

      // Nếu không có từ server, thử IndexedDB
      if ((!tnDb || tnDb.length === 0) && (!dsDb || dsDb.length === 0) && (!tlDb || tlDb.length === 0)) {
        const [tn, ds, tl] = await Promise.all([
          teacherContentService.loadDatabaseFromIndexedDB(teacherId, lop, chuong, bai, 'TN').catch(() => null),
          teacherContentService.loadDatabaseFromIndexedDB(teacherId, lop, chuong, bai, 'DS').catch(() => null),
          teacherContentService.loadDatabaseFromIndexedDB(teacherId, lop, chuong, bai, 'TL').catch(() => null)
        ])
        tnDb = tn
        dsDb = ds
        tlDb = tl
      }

      // Load quiz với teacher databases
      const quiz = await bankQuestionService.getRandomQuestions(
        lop,
        chuong,
        bai,
        {
          thoiGianLamBai: 15,
          diemDat: 70
        },
        teacherId,
        { TN: tnDb, DS: dsDb, TL: tlDb }
      )

      setResults(prev => ({
        ...prev,
        quiz
      }))

      console.log('✅ [Test Part 4] Teacher quiz loaded:', {
        totalQuestions: quiz.cauHoi.length,
        title: quiz.tieuDe
      })
    } catch (error: any) {
      console.error('❌ [Test Part 4] Error loading teacher quiz:', error)
      setResults(prev => ({
        ...prev,
        error: `Quiz error: ${error.message}`
      }))
    } finally {
      setLoading(false)
    }
  }

  // ✅ FIX: Upload database và lưu tạm vào IndexedDB (không upload lên host)
  const handleFileSelect = (type: 'TN' | 'DS' | 'TL', file: File | null) => {
    if (file) {
      setUploadedFiles(prev => ({ ...prev, [type]: file }))
      setUploadResults(prev => ({ ...prev, [type]: undefined })) // Reset result
    }
  }

  const uploadDatabaseToIndexedDB = async (type: 'TN' | 'DS' | 'TL') => {
    if (!teacherId) {
      toast.error('Vui lòng nhập Teacher ID')
      return
    }

    const file = uploadedFiles[type]
    if (!file) {
      toast.error(`Vui lòng chọn file ${type}.db`)
      return
    }

    setUploading(true)
    try {
      console.log(`📤 [Test Part 4] Uploading ${type} database to IndexedDB (temporary, not to host)...`)
      
      // ✅ FIX: Sử dụng uploadQuestionBank để lưu vào IndexedDB (không upload lên host)
      await teacherContentService.uploadQuestionBank(
        teacherId,
        lop,
        chuong,
        bai,
        type,
        file
      )

      setUploadResults(prev => ({
        ...prev,
        [type]: `✅ Đã lưu ${type}.db vào IndexedDB (${formatBytes(file.size)})`
      }))

      toast.success(`✅ Đã lưu ${type}.db vào IndexedDB thành công!`)
      console.log(`✅ [Test Part 4] ${type} database saved to IndexedDB`)
    } catch (error: any) {
      console.error(`❌ [Test Part 4] Error uploading ${type} database:`, error)
      setUploadResults(prev => ({
        ...prev,
        [type]: `❌ Lỗi: ${error.message}`
      }))
      toast.error(`❌ Lỗi upload ${type}.db: ${error.message}`)
    } finally {
      setUploading(false)
    }
  }

  const uploadAllDatabases = async () => {
    if (!teacherId) {
      toast.error('Vui lòng nhập Teacher ID')
      return
    }

    const types: Array<'TN' | 'DS' | 'TL'> = ['TN', 'DS', 'TL']
    const filesToUpload = types.filter(type => uploadedFiles[type])

    if (filesToUpload.length === 0) {
      toast.error('Vui lòng chọn ít nhất một file database')
      return
    }

    setUploading(true)
    const results: { [key: string]: string } = {}

    try {
      for (const type of filesToUpload) {
        const file = uploadedFiles[type]
        if (file) {
          try {
            console.log(`📤 [Test Part 4] Uploading ${type} database...`)
            await teacherContentService.uploadQuestionBank(
              teacherId,
              lop,
              chuong,
              bai,
              type,
              file
            )
            results[type] = `✅ ${type}.db (${formatBytes(file.size)})`
            toast.success(`✅ Đã lưu ${type}.db`)
          } catch (error: any) {
            results[type] = `❌ ${type}.db: ${error.message}`
            console.error(`❌ Error uploading ${type}:`, error)
          }
        }
      }

      setUploadResults(results)
      toast.success(`✅ Đã lưu ${filesToUpload.length} database(s) vào IndexedDB!`)
    } finally {
      setUploading(false)
    }
  }

  // ✅ FIX: Làm bài với database đã upload (từ IndexedDB)
  const startQuizWithUploadedDatabase = () => {
    if (!teacherId) {
      toast.error('Vui lòng nhập Teacher ID')
      return
    }

    // Navigate đến trang làm bài với teacherId
    const url = `/trac-nghiem/${lop}/${chuong}/${bai}?teacher=${teacherId}`
    console.log(`🎯 [Test Part 4] Navigating to quiz page: ${url}`)
    navigate(url)
  }

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 bytes'
    const k = 1024
    const sizes = ['bytes', 'KB', 'MB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Database className="w-8 h-8 text-purple-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Test Database Part 4</h1>
              <p className="text-sm text-gray-600">Test chức năng load database từ server (TN.db, DS.db, TL.db)</p>
            </div>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
            <p className="text-sm text-yellow-800">
              <strong>⚠️ Lưu ý:</strong> Trang này chỉ test phần 4 (database). Không ảnh hưởng đến phần 1 (theory/images), phần 2 (YouTube videos), phần 3 (simulation videos).
            </p>
          </div>

          {/* ✅ FIX: Upload Database Section - Lưu tạm vào IndexedDB (không upload lên host) */}
          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 mb-6 border-2 border-green-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Upload className="w-5 h-5 text-green-600" />
              Bước 1: Upload Database (Lưu tạm vào IndexedDB)
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Upload các file database (TN.db, DS.db, TL.db) và lưu tạm vào IndexedDB. <strong>KHÔNG upload lên host.</strong>
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {(['TN', 'DS', 'TL'] as const).map((type) => (
                <div key={type} className="bg-white rounded-lg p-4 border-2 border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {type}.db
                  </label>
                  <input
                    ref={fileInputRefs[type]}
                    type="file"
                    accept=".db,.sqlite,.sqlite3"
                    onChange={(e) => handleFileSelect(type, e.target.files?.[0] || null)}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRefs[type].current?.click()}
                    className="w-full px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm text-gray-700 mb-2 transition-colors"
                  >
                    {uploadedFiles[type] ? uploadedFiles[type].name : 'Chọn file...'}
                  </button>
                  {uploadedFiles[type] && (
                    <button
                      onClick={() => uploadDatabaseToIndexedDB(type)}
                      disabled={uploading}
                      className="w-full px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                    >
                      {uploading ? <Loader className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      Lưu vào IndexedDB
                    </button>
                  )}
                  {uploadResults[type] && (
                    <p className={`text-xs mt-2 ${uploadResults[type]?.startsWith('✅') ? 'text-green-600' : 'text-red-600'}`}>
                      {uploadResults[type]}
                    </p>
                  )}
                </div>
              ))}
            </div>

            <button
              onClick={uploadAllDatabases}
              disabled={uploading || Object.keys(uploadedFiles).length === 0}
              className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {uploading ? <Loader className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
              Upload Tất Cả Database Vào IndexedDB
            </button>
          </div>

          {/* ✅ FIX: Làm bài với database đã upload */}
          <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-lg p-6 mb-6 border-2 border-orange-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Play className="w-5 h-5 text-orange-600" />
              Bước 2: Làm Bài Với Database Đã Upload
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Làm bài với database đã upload vào IndexedDB (không dùng database mặc định).
            </p>
            <button
              onClick={startQuizWithUploadedDatabase}
              disabled={!teacherId}
              className="w-full px-4 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              <FileText className="w-5 h-5" />
              Làm Bài Với Database Đã Upload
            </button>
          </div>

          {/* Input Form */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Teacher ID</label>
              <input
                type="text"
                value={teacherId}
                onChange={(e) => setTeacherId(e.target.value)}
                placeholder="ef102249-917f-4697-8ddd-4b95cdc3ede7"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lớp</label>
              <input
                type="text"
                value={lop}
                onChange={(e) => setLop(e.target.value)}
                placeholder="11"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Chương</label>
              <input
                type="text"
                value={chuong}
                onChange={(e) => setChuong(e.target.value)}
                placeholder="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bài</label>
              <input
                type="text"
                value={bai}
                onChange={(e) => setBai(e.target.value)}
                placeholder="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Test Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <button
              onClick={testServerDatabases}
              disabled={loading}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Test Server DB
            </button>
            <button
              onClick={testIndexedDBDatabases}
              disabled={loading}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
              Test IndexedDB
            </button>
            <button
              onClick={testSystemDatabases}
              disabled={loading}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? <Loader className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Test System DB
            </button>
            <button
              onClick={testTeacherQuiz}
              disabled={loading}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? <Loader className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              Test Teacher Quiz
            </button>
          </div>

          {/* Results */}
          {results.error && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
              <p className="text-red-800 font-semibold">❌ Error:</p>
              <p className="text-red-700">{results.error}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {/* Server Results */}
            <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
              <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                <Download className="w-5 h-5" />
                Server Databases
              </h3>
              {results.server ? (
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-700">TN.db:</span>
                    <span className="font-semibold text-blue-700">
                      {results.server.TN ? formatBytes(results.server.TN) : 'Not found'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">DS.db:</span>
                    <span className="font-semibold text-blue-700">
                      {results.server.DS ? formatBytes(results.server.DS) : 'Not found'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">TL.db:</span>
                    <span className="font-semibold text-blue-700">
                      {results.server.TL ? formatBytes(results.server.TL) : 'Not found'}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-sm">Chưa test</p>
              )}
            </div>

            {/* IndexedDB Results */}
            <div className="bg-green-50 rounded-lg p-4 border-2 border-green-200">
              <h3 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                <Database className="w-5 h-5" />
                IndexedDB Databases
              </h3>
              {results.indexedDB ? (
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-700">TN.db:</span>
                    <span className="font-semibold text-green-700">
                      {results.indexedDB.TN ? formatBytes(results.indexedDB.TN) : 'Not found'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">DS.db:</span>
                    <span className="font-semibold text-green-700">
                      {results.indexedDB.DS ? formatBytes(results.indexedDB.DS) : 'Not found'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">TL.db:</span>
                    <span className="font-semibold text-green-700">
                      {results.indexedDB.TL ? formatBytes(results.indexedDB.TL) : 'Not found'}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-sm">Chưa test</p>
              )}
            </div>

            {/* System Results */}
            <div className="bg-purple-50 rounded-lg p-4 border-2 border-purple-200">
              <h3 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
                <RefreshCw className="w-5 h-5" />
                System Databases
              </h3>
              {results.system ? (
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-700">TN questions:</span>
                    <span className="font-semibold text-purple-700">{results.system.TN || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">DS questions:</span>
                    <span className="font-semibold text-purple-700">{results.system.DS || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">TL questions:</span>
                    <span className="font-semibold text-purple-700">{results.system.TL || 0}</span>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-sm">Chưa test</p>
              )}
            </div>
          </div>

          {/* Quiz Results */}
          {results.quiz && (
            <div className="bg-orange-50 rounded-lg p-4 border-2 border-orange-200">
              <h3 className="font-semibold text-orange-900 mb-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                Quiz Data
              </h3>
              <div className="space-y-2">
                <div>
                  <span className="text-gray-700 font-medium">Title:</span>
                  <span className="ml-2 text-orange-700">{results.quiz.tieuDe}</span>
                </div>
                <div>
                  <span className="text-gray-700 font-medium">Total Questions:</span>
                  <span className="ml-2 text-orange-700 font-semibold">{results.quiz.cauHoi.length}</span>
                </div>
                <div>
                  <span className="text-gray-700 font-medium">Time Limit:</span>
                  <span className="ml-2 text-orange-700">{results.quiz.gioi_han_thoi_gian / 60} minutes</span>
                </div>
                <div>
                  <span className="text-gray-700 font-medium">Passing Score:</span>
                  <span className="ml-2 text-orange-700">{results.quiz.diem_dat}%</span>
                </div>
                {results.quiz.cauHoi.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Sample Questions:</p>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {results.quiz.cauHoi.slice(0, 5).map((q, idx) => (
                        <div key={idx} className="bg-white p-2 rounded text-sm">
                          <p className="font-medium text-gray-900">Q{idx + 1}: {q.cau_hoi.substring(0, 100)}...</p>
                          <p className="text-xs text-gray-500">Type: {q.type} | Level: {q.muc_do}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default TestDatabasePart4

